/**
 * BookingFlows Connect — PUBLIC flow storefront (/c/:flowSlug).
 * This is the page behind every direct URL + iframe embed an affiliate shares.
 * Prices shown here already include the affiliate's markup — exactly what
 * checkout charges. Location is always visible so nobody books a Dubai jet
 * ski thinking it's in Texas.
 */
import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { API_URL } from '../config/api';
import { MapPin, Clock, Loader2, Waypoints } from 'lucide-react';

const SYM = { USD: '$', AED: 'د.إ ', GBP: '£', EUR: '€' };
const money = (v, cur = 'USD') => `${SYM[cur] || `${cur} `}${Number(v || 0).toFixed(2)}`;

export default function ConnectStorefront() {
  const { flowSlug } = useParams();
  const [searchParams] = useSearchParams();
  const embedded = searchParams.get('embed') === '1';
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/v2/public/connect/flows/${flowSlug}`)
      .then((r) => { if (!r.ok) throw new Error('not found'); return r.json(); })
      .then(setData)
      .catch(() => setError(true));
  }, [flowSlug]);

  const book = (svc) => {
    // Full checkout path (services-grid → /public/bookings) carries the Connect
    // markup + application-fee recoup; directbook opens the modal immediately.
    const url = `/services-grid.html?org=${encodeURIComponent(svc.operator.slug)}&service=${encodeURIComponent(svc.id)}&directbook=true&ref=${encodeURIComponent(flowSlug)}`;
    window.open(url, '_blank', 'noopener');
  };

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white px-6 text-center" style={{ fontFamily: "'Manrope', 'Inter', sans-serif" }} data-testid="connect-storefront-notfound">
        <Waypoints className="w-10 h-10 text-slate-300 mb-3" />
        <p className="text-lg font-extrabold text-slate-800">This collection isn't available</p>
        <p className="text-sm text-slate-400 mt-1">The link may have been paused or removed.</p>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-[#FAFDFB] ${embedded ? '' : ''}`} style={{ fontFamily: "'Manrope', 'Inter', sans-serif" }} data-testid="connect-storefront">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {!data ? (
          <div className="flex justify-center py-24"><Loader2 className="w-7 h-7 animate-spin text-emerald-300" /></div>
        ) : (
          <>
            <div className="mb-8 text-center">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-600 mb-2">Curated experiences</p>
              <h1 className="text-2xl sm:text-4xl font-extrabold tracking-tight text-slate-900" data-testid="connect-storefront-title">{data.flow.name}</h1>
              {data.affiliate?.name && (
                <p className="text-sm text-slate-400 mt-2">hand-picked by <span className="font-bold text-slate-600">{data.affiliate.name}</span></p>
              )}
            </div>

            {data.services.length === 0 && (
              <p className="text-center text-sm text-slate-400 py-16">No experiences in this collection yet — check back soon.</p>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="connect-storefront-grid">
              {data.services.map((svc) => (
                <div key={svc.id} className="rounded-2xl bg-white border border-emerald-900/5 shadow-[0_2px_16px_-6px_rgba(5,150,105,0.12)] overflow-hidden flex flex-col hover:shadow-[0_12px_36px_-12px_rgba(5,150,105,0.3)] transition-shadow duration-200" data-testid={`storefront-card-${svc.id}`}>
                  <div className="relative h-48 bg-gradient-to-br from-emerald-100 to-teal-50">
                    {(svc.images || [])[0] && <img src={svc.images[0]} alt={svc.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />}
                    <div className="absolute top-3 left-3 flex items-center gap-1 rounded-full bg-white/90 backdrop-blur px-2.5 py-1 text-[11px] font-extrabold text-slate-700 shadow-sm">
                      <MapPin className="w-3 h-3 text-emerald-600" />
                      {[svc.city, svc.country].filter(Boolean).join(', ') || 'See details'}
                    </div>
                  </div>
                  <div className="p-5 flex flex-col flex-1">
                    <p className="text-base font-extrabold text-slate-900 leading-snug">{svc.name}</p>
                    {svc.shortDescription && <p className="text-xs text-slate-500 mt-1.5 leading-relaxed line-clamp-2">{svc.shortDescription}</p>}
                    <p className="text-[11px] text-slate-400 mt-2">by {svc.operator.name}</p>
                    <div className="mt-auto pt-4 flex items-center justify-between gap-3">
                      <div>
                        <p className="text-lg font-extrabold text-slate-900" style={{ fontVariantNumeric: 'tabular-nums' }}>{money(svc.price, svc.currency)}</p>
                        {svc.durationMinutes ? (
                          <p className="text-[11px] text-slate-400 flex items-center gap-1"><Clock className="w-3 h-3" />{svc.durationMinutes >= 60 ? `${Math.round(svc.durationMinutes / 60)} hour${svc.durationMinutes >= 120 ? 's' : ''}` : `${svc.durationMinutes} min`}</p>
                        ) : null}
                      </div>
                      <button
                        type="button"
                        onClick={() => book(svc)}
                        data-testid={`storefront-book-${svc.id}`}
                        className="rounded-full bg-emerald-600 text-white text-sm font-extrabold px-5 py-2.5 hover:bg-emerald-700 transition-colors"
                      >
                        Book Now
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-10 flex items-center justify-center gap-1.5 text-[11px] text-slate-400">
              <Waypoints className="w-3.5 h-3.5 text-emerald-500" />
              Powered by <a href="https://bookingflows.com" target="_blank" rel="noreferrer" className="font-bold text-slate-500 hover:text-emerald-600">BookingFlows</a>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
